// Developer: Ankit Gupta
/**
 * The product's icon rail.
 *
 * The menu style of the AAFAQ assistant: a narrow rail of square buttons, each
 * an icon over a small label. A button that is a page goes straight there. A
 * button that is a section opens a panel beside the rail listing its pages:
 * on hover with a pointer, on tap without one. The panel closes when the
 * pointer leaves the rail, when a page is chosen, on Escape, or on a click
 * anywhere else.
 *
 * DataPilot's shell, Insight's and Confy's all render their menu through this,
 * so the rail looks and behaves the same on every side.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { SideNav, type NavEntry } from './SideNav';

export interface RailSection {
  key: string;
  label: string;
  icon: ReactNode;
  /** A section that is one page: the button goes there, no panel. */
  path?: string;
  /** A section of pages: the button opens a panel listing them. */
  entries?: NavEntry[];
}

export interface RailDivider {
  key: string;
  divider: true;
}

export type RailItem = RailSection | RailDivider;

interface RailNavProps {
  items: RailItem[];
  /** Whether a nav path is the one currently being viewed. */
  isActive: (path: string) => boolean;
  onNavigate: (path: string) => void;
  /**
   * The href to put on a link, when it differs from the path routed to.
   * Insight and Confy carry their scope in the query string.
   */
  hrefFor?: (path: string) => string;
}

function isDivider(item: RailItem): item is RailDivider {
  return (item as RailDivider).divider === true;
}

function entryActive(entry: NavEntry, isActive: (path: string) => boolean): boolean {
  if (entry.path && isActive(entry.path)) return true;
  return entry.children?.some((c) => isActive(c.path)) ?? false;
}

function plainClick(e: React.MouseEvent, go: () => void) {
  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
  e.preventDefault();
  go();
}

/**
 * A rail button for one page, as an element rather than a component: antd's
 * Tooltip clones its child to attach a ref, which a function component cannot
 * receive. It is a real anchor with a real href, so it can be tabbed to,
 * opened in a new tab and copied, while a plain click still routes in-app.
 */
export function railLink({
  href,
  onNavigate,
  icon,
  label,
  active = false,
  danger = false,
}: {
  href: string;
  onNavigate: () => void;
  icon: ReactNode;
  label: string;
  active?: boolean;
  danger?: boolean;
}) {
  return (
    <a
      className={`dp-rail-item${active ? ' is-active' : ''}${danger ? ' is-danger' : ''}`}
      href={href}
      aria-current={active ? 'page' : undefined}
      onClick={(e) => plainClick(e, onNavigate)}
    >
      {icon}
      <span className="dp-rail-label">{label}</span>
    </a>
  );
}

export function RailNav({ items, isActive, onNavigate, hrefFor }: RailNavProps) {
  const [open, setOpen] = useState<string | null>(null);
  const root = useRef<HTMLDivElement | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  // The pointer crosses the rail's border on its way into the panel, which
  // fires a leave; a short grace keeps the panel open across it.
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpen(null), 140);
  };
  useEffect(() => cancelClose, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(null);
    };
    const onPointerDown = (e: PointerEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(null);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('pointerdown', onPointerDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onPointerDown);
    };
  }, [open]);

  const href = (path: string) => (hrefFor ? hrefFor(path) : path);
  const opened = items.find((s): s is RailSection => !isDivider(s) && s.key === open && !!s.entries);

  return (
    <div ref={root} className="dp-rail-nav" onMouseEnter={cancelClose} onMouseLeave={scheduleClose}>
      <ul className="dp-rail-list">
        {items.map((item) => {
          if (isDivider(item)) {
            return (
              <li key={item.key} aria-hidden="true">
                <div className="dp-rail-divider" />
              </li>
            );
          }
          if (item.entries) {
            const active = item.entries.some((e) => entryActive(e, isActive));
            return (
              <li key={item.key}>
                <button
                  type="button"
                  className={`dp-rail-item${active ? ' is-active' : ''}${open === item.key ? ' is-open' : ''}`}
                  aria-haspopup="true"
                  aria-expanded={open === item.key}
                  onMouseEnter={() => {
                    cancelClose();
                    setOpen(item.key);
                  }}
                  onClick={() => setOpen(item.key)}
                >
                  {item.icon}
                  <span className="dp-rail-label">{item.label}</span>
                </button>
              </li>
            );
          }
          const path = item.path!;
          return (
            <li key={item.key} onMouseEnter={() => setOpen(null)}>
              {railLink({
                href: href(path),
                onNavigate: () => onNavigate(path),
                icon: item.icon,
                label: item.label,
                active: isActive(path),
              })}
            </li>
          );
        })}
      </ul>

      {opened && (
        <div className="dp-flyout" role="region" aria-label={opened.label}>
          <div className="dp-flyout-head">
            {opened.icon}
            <span>{opened.label}</span>
          </div>
          <div className="dp-flyout-body">
            <SideNav
              entries={opened.entries!}
              collapsed={false}
              isActive={isActive}
              onNavigate={(path) => {
                setOpen(null);
                onNavigate(path);
              }}
              hrefFor={hrefFor}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default RailNav;
